import { useEffect, useMemo, useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import TopBar from './components/layout/TopBar';
import Sidebar from './components/layout/Sidebar';
import GettingStartedPage from './pages/GettingStartedPage';
import SSOCallbackPage from './pages/SSOCallbackPage';
import EndpointPage from './pages/EndpointPage';
import ScenariosListPage from './pages/ScenariosListPage';
import ScenarioPage from './pages/ScenarioPage';
import CommandPalette from './components/palette/CommandPalette';
import { useApiKey } from './stores/apiKey.store';
import { useOpenApi } from './hooks/useOpenApi';
import { usePaletteShortcut } from './hooks/usePaletteShortcut';
import { listEndpoints } from './lib/openapi/parse';
import type { Method } from './lib/tokens';

export default function App() {
    const loadFromStorage = useApiKey((s) => s.loadFromStorage);
    const { spec } = useOpenApi();
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        loadFromStorage();
        // Best-effort cleanup of legacy demo state from past sessions.
        try {
            localStorage.removeItem('playground_demo_mode');
        } catch { /* ignore */ }
    }, [loadFromStorage]);

    usePaletteShortcut(() => setPaletteOpen(true));

    const endpoints = useMemo(() => {
        if (!spec) return [];
        return listEndpoints(spec).map((e) => ({
            operationId: e.operationId,
            method: e.method.toUpperCase() as Method,
            path: e.path,
            summary: e.summary,
            tag: e.tag,
        }));
    }, [spec]);

    return (
        <div className="min-h-screen bg-bg text-ink">
            <TopBar
                onSearchClick={() => setPaletteOpen(true)}
                onMenuClick={() => setSidebarOpen(true)}
            />
            <div className="flex">
                <Sidebar
                    endpoints={endpoints}
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                />
                <main className="flex-1 min-w-0">
                    <Routes>
                        <Route path="/" element={<GettingStartedPage />} />
                        <Route path="/sso/callback" element={<SSOCallbackPage />} />
                        <Route path="/r/:operationId" element={<EndpointPage />} />
                        <Route path="/scenarios" element={<ScenariosListPage />} />
                        <Route path="/scenarios/:id" element={<ScenarioPage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </main>
            </div>
            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </div>
    );
}

function NotFound() {
    return (
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
            <h1 className="text-2xl font-semibold mb-2" style={{ color: 'var(--ink)' }}>Halaman tidak ditemukan</h1>
            <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                Pilih endpoint dari sidebar untuk mulai eksplorasi.
            </p>
            <a
                href="/"
                className="inline-flex items-center gap-1.5 text-sm font-medium"
                style={{ color: 'var(--accent)' }}
            >
                ← Kembali ke Getting Started
            </a>
        </div>
    );
}
